<template>
  <div class="comparison-profile-header">
    <div class="profile-header-cell">
      <button
        type="button"
        class="profile-header-remove-btn"
        aria-label="Remove column from comparison"
        @click.stop="handleRemove"
      >
        <v-icon size="14" icon="mdi-close" />
      </button>
      <div class="d-flex align-center flex-wrap ga-1 mb-2 profile-header-title-row">
        <span class="d-flex align-center ga-1">
          <v-icon
            size="18"
            icon="material-symbols:drag-indicator"
            color="textLight"
          />
          <span class="text-break text-h2 text-textDarkest">{{ header }}</span>
        </span>
        <span
          v-if="sourceLabel"
          class="profile-header-type-pill text-body-3 text-textLight px-2"
        >
          {{ sourceLabel }}
        </span>
      </div>
      <dl
        v-if="showMetadata"
        class="profile-header-meta"
      >
        <template v-if="jobCode">
          <dt>Job Code</dt>
          <dd>{{ jobCode }}</dd>
        </template>
        <template v-if="jobLevel">
          <dt>Job Level</dt>
          <dd>{{ jobLevel }}</dd>
        </template>
        <template v-if="jobFamilyNames">
          <dt>Job Family</dt>
          <dd>{{ jobFamilyNames }}</dd>
        </template>
        <template v-if="showEmployeesRow">
          <dt>{{ employeesRowLabel }}</dt>
          <dd class="profile-header-employees-dd">
            <span v-if="!isStandaloneJobNav">{{ employeeCountDisplay }}</span>
            <HideRbac :permissions="['viewJobProfileArchitecture']">
              <span
                v-for="code in sortedAlertCodes"
                :key="code"
                class="profile-header-alert-icon"
                @click.stop
              >
                <DpplTooltip
                  tooltip-placement="bottom"
                  :tooltip-offset="12"
                  max-width="360px"
                  teleport
                >
                  <template #activator="{ onMouseenter, onMouseleave }">
                    <span
                      class="profile-header-alert-activator"
                      :aria-label="getArchitectureAlertPillLabel(code)"
                      @mouseenter="(event) => handleAlertIconHover(onMouseenter, event)"
                      @mouseleave="onMouseleave"
                    >
                      <span class="profile-header-alert-badge">
                        <v-icon
                          size="18"
                          icon="mdi-alert-circle-outline"
                        />
                      </span>
                    </span>
                  </template>
                  <template #tooltip-content>
                    <div class="architecture-alert-tooltip-content">
                      <span class="architecture-alert-tooltip-text">{{ getTooltipText(code) }}</span>
                      <a
                        href="#"
                        class="architecture-alert-tooltip-link"
                        :data-beacon-article-sidebar="ARCHITECTURE_ALERT_LEARN_MORE_ARTICLE_ID"
                        @click.stop
                      >Learn more here</a>.
                    </div>
                  </template>
                </DpplTooltip>
              </span>
            </HideRbac>
          </dd>
        </template>
      </dl>
      <div class="pl-5 profile-header-editor-row">
        <router-link
          :to="editorRoute"
          class="profile-header-editor-link"
          target="_blank"
          @click.stop="handleOpenEditor"
        >
          Open in editor
        </router-link>
        <button
          type="button"
          class="profile-header-comment-btn mt-1"
          aria-label="View comments"
          @click.stop="$emit('open-sidebar')"
        >
          <DpplSvg file-name="icons/Icon_Comment" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import DpplSvg from '@/components/datapeople/DpplSvg.vue';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import HideRbac from '@/components/rbac/HideRbac.vue';
import { useTracking } from '@/stores/tracking/tracking';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import {
  ARCHITECTURE_ALERT_LEARN_MORE_ARTICLE_ID,
  getAlertCategoryForCode,
  getArchitectureAlertPillLabel,
  getArchitectureAlertTooltip,
  sortArchitectureAlertCodes,
} from '@/components/jobArchitecture/alertHelpers';

const props = defineProps({
  header: { type: String, default: '' },
  jobCode: { type: String, default: '' },
  jobLevel: { type: String, default: '' },
  jobFamilyNames: { type: String, default: '' },
  employeeCountDisplay: { type: String, default: '' },
  profileId: { type: String, required: true },
  source: { type: String, default: 'hris' },
  showMetadata: { type: Boolean, default: false },
  alertCodes: {
    type: Array,
    default: () => [],
  },
  tooltipContext: {
    type: Object,
    default: () => ({ familyNames: [], groupNames: [] }),
  },
});

const emit = defineEmits(['open-sidebar', 'remove']);

const { hrisDisplayName, isStandaloneJobNav } = storeToRefs(useAppAuthData());

const isDraft = computed(() => props.source === 'draft');

const sourceLabel = computed(() => {
  if (isDraft.value) return 'Draft';
  if (props.source === 'hris') return hrisDisplayName.value;

  return '';
});

const showEmployeeCount = computed(() => !isDraft.value);

const sortedAlertCodes = computed(() => sortArchitectureAlertCodes(props.alertCodes));

// Standalone has no employee data yet; still show architecture alerts when present.
const showEmployeesRow = computed(() => {
  if (!showEmployeeCount.value) return false;
  if (!isStandaloneJobNav.value) return true;

  return sortedAlertCodes.value.length > 0;
});

const employeesRowLabel = computed(() => (
  isStandaloneJobNav.value ? 'Alerts' : 'Employees'
));

const editorRoute = computed(() => ({
  name: isDraft.value ? 'job-profile-view' : 'job-profile-hris-view',
  params: { id: props.profileId },
}));

const handleRemove = () => {
  useTracking().jaMatrixRemovedJobProfile();
  emit('remove');
};

const handleOpenEditor = () => {
  useTracking().jaMatrixOpenedJobProfileFromHeader();
};

const handleAlertIconHover = (onMouseenter, event) => {
  useTracking().jaMatrixHoveredAlertIcon();
  onMouseenter(event);
};

const getAssignmentNames = (code) => {
  const category = getAlertCategoryForCode(code);

  if (category === 'multiple_assignments') {
    return props.tooltipContext.familyNames;
  }

  return [];
};

const getTooltipText = (code) => getArchitectureAlertTooltip({
  code,
  nodeType: 'profile',
  assignmentNames: getAssignmentNames(code),
});
</script>

<style scoped lang="less">
.comparison-profile-header {
  position: relative;
}

.profile-header-cell {
  position: relative;
}

.profile-header-title-row {
  padding-right: 28px;
}

.profile-header-type-pill {
  border-radius: 100px;
  border: 1px solid @stroke-default;
}

.profile-header-meta {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 16px;
  row-gap: 4px;
  margin: 0 0 10px;
  padding-left: 20px;
  font-size: 12px;
  line-height: 16px;
}

.profile-header-meta dt {
  font-weight: 600;
  color: @text-darkest;
  white-space: nowrap;
}

.profile-header-meta dd {
  margin: 0;
  font-weight: 400;
  color: @text-light;
  word-break: break-word;
}

.profile-header-employees-dd {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.profile-header-editor-link {
  color: @text-brand;
  text-decoration: none;
  font-weight: 400;
}

.profile-header-editor-link:hover {
  text-decoration: underline;
}

.profile-header-editor-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.profile-header-comment-btn {
  width: 18px;
  height: 18px;
  color: @text-light;
  cursor: pointer;
  padding: 0;
  border: none;
  background: transparent;
}

.profile-header-comment-btn:hover {
  color: @text-darkest;
}

.profile-header-comment-btn :deep(svg) {
  width: 14px;
  height: 14px;
}

.profile-header-alert-icon {
  display: inline-flex;
  align-items: center;
  // Keep the warning badge from pushing the employee count down.
  height: 16px;
  min-height: 0;

  :deep(.dppl-dark-tooltip),
  :deep(.activator) {
    display: inline-flex;
    align-items: center;
    width: auto;
  }
}

.profile-header-alert-activator {
  display: inline-flex;
  align-items: center;
  cursor: default;
}

.profile-header-alert-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: @yellow-100;
  color: @yellow-500;
}

.profile-header-remove-btn {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  background: transparent;
  color: @text-light;
  cursor: pointer;
  border-radius: 4px;
}

.profile-header-remove-btn:hover {
  color: @text-darkest;
  background: fade(@grey-900, 6%);
}
</style>

<style lang="less">
.architecture-alert-tooltip-content {
  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  color: @text-dark;
}

.architecture-alert-tooltip-link {
  font-size: 13px;
  font-weight: 500;
  color: @text-brand;
  margin-left: 4px;
}
</style>
